<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import ApostaTudoHeader from '~/components/casino/games/ApostaTudoHeader.vue';

const route = useRoute();
const casino = useCasinoStore();
const { isAuthenticated } = storeToRefs(useAuthStore());

// Hide footer while this component is active (infinite scroll page)
casino.hasMoreGamesToLoad = true;
onUnmounted(() => {
  casino.hasMoreGamesToLoad = false;
});

const games: any = ref({ data: [] });
const callCount = ref<number>(0);
const headerRef = ref<InstanceType<typeof ApostaTudoHeader> | null>(null);
const isDeferredSearch = ref<boolean>(false);
const showFilterSkeleton = ref<boolean>(false);
const gridContainerRef = ref<HTMLElement | null>(null);
const showStickyBar = ref<boolean>(false);
const observerTarget = ref<HTMLElement | null>(null);
const stickyBarTop = ref<number>(0);
const isMobile = ref<boolean>(false);
const loadMoreTrigger = ref<HTMLElement | null>(null);
const IMAGE_LOAD_TIMEOUT = 1200; // max wait for images (ms)

// Sync lock: blocks duplicate firings between the moment loadMoreGames is
// invoked and the moment isLoadingMoreGames flips inside fetchGames.
let loadMoreLock = false;

// Circuit breaker: stop auto-loading after consecutive fetch failures so a
// broken filter or network outage doesn't loop forever (skeleton appearing
// and disappearing shifts layout, which keeps re-firing the IntersectionObserver).
const MAX_CONSECUTIVE_FAILURES = 3;
const consecutiveFailures = ref<number>(0);
const loadMoreDisabled = ref<boolean>(false);

/**
 * Wait until visible images inside the grid are loaded (or timeout).
 * Returns a promise that resolves when ready to unblur.
 */
const waitForImages = (): Promise<void> => {
  return new Promise((resolve) => {
    nextTick(() => {
      const container = gridContainerRef.value;

      if (!container) {
        resolve();

        return;
      }

      const imgs = Array.from(container.querySelectorAll('img'));
      const pending = imgs.filter((img) => !img.complete);

      if (pending.length === 0) {
        resolve();

        return;
      }

      let settled = false;
      const settle = () => {
        if (settled) return;
        settled = true;
        resolve();
      };

      // Timeout fallback
      const timer = setTimeout(settle, IMAGE_LOAD_TIMEOUT);

      let loaded = 0;
      const onDone = () => {
        loaded++;
        if (loaded >= pending.length) {
          clearTimeout(timer);
          settle();
        }
      };

      pending.forEach((img) => {
        img.addEventListener('load', onDone, { once: true });
        img.addEventListener('error', onDone, { once: true });
      });
    });
  });
};

const onDeferredSearch = (v: boolean) => {
  isDeferredSearch.value = v;
  if (v) {
    // Show skeleton immediately when fetch starts
    showFilterSkeleton.value = true;
  }
  // When v === false (fetch done), skeleton is removed later by waitForImages in updateGamesList
};

const scrollToTop = () => {
  try {
    // Find the search input element
    const searchInput = document.querySelector(
      'input[placeholder="Procure jogos ou provedoras"]',
    ) as HTMLInputElement;

    if (searchInput) {
      // Scroll to the input smoothly
      searchInput.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });

      // Focus on the input after a short delay to ensure scroll completes
      setTimeout(() => {
        searchInput.focus();
      }, 300);
    } else if (observerTarget.value) {
      // Fallback to observerTarget
      observerTarget.value.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  } catch (e) {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

const calculateHeaderHeight = () => {
  // Try to find the main header element
  const possibleHeaders = [
    document.querySelector('#divPageHeader'),
    document.querySelector('header'),
    document.querySelector('.gamesBar'), // From Bar.vue component
    document.querySelector('[class*="header"]'),
    document.querySelector('[class*="Header"]'),
  ].filter(Boolean);

  let maxHeight = 0;

  possibleHeaders.forEach((header) => {
    if (header) {
      const rect = header.getBoundingClientRect();
      const styles = window.getComputedStyle(header);
      const position = styles.position;

      // For fixed or sticky headers, use their bottom position
      if (position === 'fixed' || position === 'sticky') {
        maxHeight = Math.max(maxHeight, rect.bottom);
      }
      // For non-fixed headers at the top of the page, use their height
      else if (rect.top <= 0) {
        maxHeight = Math.max(maxHeight, rect.height);
      }
    }
  });

  // Fallback to a reasonable default if no header found
  return maxHeight > 0 ? maxHeight : 60;
};

onMounted(() => {
  // Check if is mobile (screens smaller than 1024px)
  const checkIsMobile = () => {
    isMobile.value = window.innerWidth < 1024;
  };

  checkIsMobile();

  // Update on resize
  const handleResize = () => {
    checkIsMobile();
    if (!isMobile.value) {
      showStickyBar.value = false;
    }
  };

  window.addEventListener('resize', handleResize);

  const handleOnline = () => {
    consecutiveFailures.value = 0;
    loadMoreDisabled.value = false;
  };

  window.addEventListener('online', handleOnline);
  onUnmounted(() => {
    window.removeEventListener('online', handleOnline);
  });

  // Calculate header height with longer delay for production builds
  setTimeout(() => {
    stickyBarTop.value = calculateHeaderHeight();
  }, 300);

  // Recalculate on window load (for production builds)
  const handleLoad = () => {
    stickyBarTop.value = calculateHeaderHeight();
  };

  if (document.readyState === 'complete') {
    handleLoad();
  } else {
    window.addEventListener('load', handleLoad);
  }

  // Use IntersectionObserver to detect when header goes out of view
  if (observerTarget.value) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // When the target is NOT intersecting (out of view), show sticky bar
          // Only show on mobile devices
          const shouldShow = !entry.isIntersecting && isMobile.value;

          // Recalculate header height before showing sticky bar
          if (shouldShow) {
            stickyBarTop.value = calculateHeaderHeight();
          }

          showStickyBar.value = shouldShow;
        });
      },
      {
        // Trigger when the element is completely out of view
        threshold: 0,
        rootMargin: '0px',
      },
    );

    observer.observe(observerTarget.value);

    // Cleanup observer on unmount
    onUnmounted(() => {
      observer.disconnect();
      window.removeEventListener('load', handleLoad);
      window.removeEventListener('resize', handleResize);
    });
  }

  // IntersectionObserver for infinite scroll
  if (loadMoreTrigger.value) {
    const loadMoreObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            !casino.isLoadingMoreGames &&
            !casino.isLoading &&
            !loadMoreDisabled.value
          ) {
            // Block beyond page 2 for "Mais Pagos"
            const maisPagosActive = headerRef.value?.isMaisPagos;

            if (maisPagosActive && pagination.value.currentPage >= 2) {
              return;
            }

            // Load more games when the trigger element is in view
            if (pagination.value.currentPage !== pagination.value.lastPage) {
              loadMoreGames();
            }
          }
        });
      },
      {
        // Trigger early so data arrives before the user reaches the bottom
        rootMargin: '600px',
        threshold: 0,
      },
    );

    loadMoreObserver.observe(loadMoreTrigger.value);

    // Cleanup observer on unmount
    onUnmounted(() => {
      loadMoreObserver.disconnect();
    });
  }
});

const pagination = ref<any>({});

const updateGamesList = (gamesData: any) => {
  // Preserve current grid height to prevent layout collapse during update
  const container = gridContainerRef.value;

  if (container) {
    container.style.minHeight = `${container.offsetHeight}px`;
  }

  const newArr = Array.isArray(gamesData?.data)
    ? gamesData.data
    : (gamesData ?? []);

  const oldArr = Array.isArray(games.value?.data) ? games.value.data : [];

  // Detect "load more" cases: the new payload starts with the same items as
  // the current list but is longer. In that case we just push the appended
  // items so existing cards are not re-rendered (eliminates the flicker).
  const isAppend =
    oldArr.length > 0 &&
    newArr.length > oldArr.length &&
    newArr[0]?.id === oldArr[0]?.id &&
    newArr[oldArr.length - 1]?.id === oldArr[oldArr.length - 1]?.id;

  if (isAppend) {
    const appended = newArr.slice(oldArr.length);

    oldArr.push(...appended);
    Object.keys(gamesData).forEach((k) => {
      if (k !== 'data') games.value[k] = gamesData[k];
    });
  } else {
    games.value = gamesData;
  }

  callCount.value++;

  // Keep skeleton until all images are loaded, then swap to real content
  if (showFilterSkeleton.value) {
    waitForImages().then(() => {
      showFilterSkeleton.value = false;
    });
  }

  // Release min-height after new items render
  nextTick(() => {
    if (container) container.style.minHeight = '';
  });
};

const updatePagination = (paginationData: any) => {
  pagination.value = paginationData;
};

const loadMoreGames = () => {
  if (loadMoreLock) return;
  if (loadMoreDisabled.value) return;
  if (casino.isLoadingMoreGames || casino.isLoading) return;

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    loadMoreDisabled.value = true;

    return;
  }

  const maisPagosActive = headerRef.value?.isMaisPagos;

  if (maisPagosActive && pagination.value.currentPage >= 2) return;

  if (pagination.value.currentPage === pagination.value.lastPage) return;

  loadMoreLock = true;

  const promise = headerRef.value?.fetchGames({ moreGames: true });

  if (promise && typeof promise.finally === 'function') {
    promise.finally(() => {
      loadMoreLock = false;

      const failed = !!headerRef.value?.lastFetchFailed;

      if (failed) {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          loadMoreDisabled.value = true;

          return;
        }

        consecutiveFailures.value++;
        if (consecutiveFailures.value >= MAX_CONSECUTIVE_FAILURES) {
          loadMoreDisabled.value = true;
        }
      } else {
        consecutiveFailures.value = 0;
      }
    });
  } else {
    loadMoreLock = false;
  }
};

// Reset circuit breaker whenever the user starts a fresh fetch (filter change,
// search, etc) — currentPage drops back to 1, which means a new query.
watch(
  () => pagination.value.currentPage,
  (newPage, oldPage) => {
    if (newPage === 1 && oldPage !== 1) {
      consecutiveFailures.value = 0;
      loadMoreDisabled.value = false;
    }
  },
);

const isMaisPagosActive = computed(() => {
  return !!headerRef.value?.isMaisPagos;
});

const hasMorePages = computed(() => {
  // Assume there are more pages until we KNOW we're on the last page.
  if (pagination.value.lastPage == null) return true;

  // "Mais Pagos" is capped at 2 pages – allow footer once we reach that limit
  if (isMaisPagosActive.value && pagination.value.currentPage >= 2)
    return false;

  return pagination.value.currentPage < pagination.value.lastPage;
});

// Sync to store so the layout can hide the footer during infinite scroll
watch(
  hasMorePages,
  (val) => {
    casino.hasMoreGamesToLoad = val;
  },
  { immediate: true },
);

const filteredGames = computed(() => {
  if (!games.value?.data) return [];

  const fromPage = route.query.from_page;

  if (fromPage === 'refer-friend') {
    const excludedSlugs = [
      'pgsoft/fortune-snake',
      'pgsoft/fortune-dragon',
      'pgsoft/futebol-fever',
      'pgsoft/chicky-run',
      'pgsoft/piggy-gold',
    ];

    return games.value.data.filter((game: any) => {
      const gameSlug = String(game.slug || '').toLowerCase();

      return !excludedSlugs.includes(gameSlug);
    });
  }

  return games.value.data;
});
</script>

<template>
  <!-- Sticky Bar - Shows when scrolled down -->
  <ClientOnly>
    <Teleport to="body">
      <Transition name="slide-down">
        <div
          v-if="showStickyBar"
          :class="$style.stickyBar"
          :style="{ top: `${stickyBarTop}px` }"
        >
          <!-- Voltar: pill [‹ Voltar] compartilhado (o mesmo da linha das
               categorias do filtro). -->
          <BaseBackButton />
          <button
            aria-label="Voltar ao topo"
            class="w-[39px] h-[34px] p-1 flex-shrink-0 flex items-center justify-center rounded-[12px] bg-[#3F3F46]/40"
            @click="scrollToTop"
          >
            <NuxtIcon
              class="w-[18px] h-[18px] text-white self-center"
              name="filter/search"
            />
          </button>
          <div :class="$style.categoriesWrapper">
            <CasinoGamesStickyCategories
              v-if="headerRef"
              :header-ref="headerRef"
            />
          </div>
        </div>
      </Transition>
    </Teleport>
  </ClientOnly>

  <div ref="observerTarget">
    <ApostaTudoHeader
      ref="headerRef"
      @update-games-list="updateGamesList"
      @update-pagination="updatePagination"
      @update:search="onDeferredSearch"
    />
  </div>

  <div ref="gridContainerRef">
    <!-- Skeleton while filter is loading -->
    <div
      v-show="showFilterSkeleton"
      :class="$style.skeletonOverlay"
    >
      <div
        v-for="i in 24"
        :key="`filter-skel-${i}`"
        :class="$style.filterSkeletonCard"
      >
        <div :class="$style.filterSkeletonShimmer" />
      </div>
    </div>

    <!-- Real content (hidden during skeleton, images load in background) -->
    <div v-show="!showFilterSkeleton">
      <CasinoMediaList
        display-mode="list"
        :is-deferred-loading="isDeferredSearch"
        :is-loading="casino.isLoading"
        :is-loading-more-games="casino.isLoadingMoreGames"
        :items="filteredGames"
        :per-row="{ 768: 3, 1100: 4, 1200: 5, desktop: 6 }"
        :show-pay-today="isMaisPagosActive && isAuthenticated"
      />
    </div>
  </div>

  <!-- Skeleton rows only while actively fetching more games -->
  <div
    v-if="casino.isLoadingMoreGames"
    :class="$style.skeletonSpacer"
  >
    <div
      v-for="i in 18"
      :key="`spacer-${i}`"
      :class="$style.skeletonSpacerCard"
    />
  </div>

  <!-- Infinite scroll trigger element -->
  <div
    ref="loadMoreTrigger"
    class="h-4"
  ></div>
  <h1 class="text-white/60 text-center my-8 px-4 leading-tight">
    Catálogo Completo de Jogos
  </h1>
</template>

<style module lang="scss">
.stickyBar {
  position: fixed !important;
  left: 0 !important;
  right: 0 !important;
  z-index: 40 !important;
  @apply bg-bg-secondary/95 backdrop-blur-sm border-b border-white/10;
  @apply flex items-center gap-2 ps-4 pe-0 py-2;
  // top is set via inline style to accommodate dynamic header height
}

:global(.slide-down-enter-active),
:global(.slide-down-leave-active) {
  transition: all 0.3s ease-out;
}

:global(.slide-down-enter-from) {
  transform: translateY(-100%);
  opacity: 0;
}

:global(.slide-down-leave-to) {
  transform: translateY(-100%);
  opacity: 0;
}

.categoriesWrapper {
  @apply flex-1 overflow-hidden;
}

.skeletonOverlay {
  margin-bottom: 1rem;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
  }

  @media (min-width: 1024px) {
    gap: 24px;
  }

  @media (min-width: 1100px) {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }

  @media (min-width: 1200px) {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
}

.filterSkeletonCard {
  width: 100%;
  aspect-ratio: 25 / 29;
  background: #0f1724;
  border-radius: 8px;
  overflow: hidden;
}

.filterSkeletonShimmer {
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #0f1724 0%, #1a2332 40%, #0f1724 80%);
  background-size: 200% 100%;
  animation: skeletonShimmer 1.4s infinite ease-in-out;
}

@keyframes skeletonShimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

// Skeleton spacer grid that fills space below real games
.skeletonSpacer {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 12px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 24px;
  }
}

.skeletonSpacerCard {
  width: 100%;
  aspect-ratio: 25 / 29;
  background: #0f1724;
  border-radius: 8px;
  opacity: 0.5;
}
</style>
